"use client";

import { ChevronRight, LineChart, Mail } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import { formatBytes } from "@/lib/storage/calculate";
import type {
  AccountCacheInventory,
  AccountCacheTypeId,
} from "@/lib/storage/inventory";

interface AccountCacheDetailViewProps {
  account: AccountCacheInventory;
  onClearType: (type: AccountCacheTypeId) => void;
  onClearSymbol: (symbol: string) => void;
  onClearAccount: () => void;
}

function typeIcon(id: AccountCacheTypeId) {
  if (id === "mailbox") return Mail;
  return LineChart;
}

export function AccountCacheDetailView({
  account,
  onClearType,
  onClearSymbol,
  onClearAccount,
}: AccountCacheDetailViewProps) {
  const marketType = account.types.find((t) => t.id === "marketData");
  const mailboxType = account.types.find((t) => t.id === "mailbox");

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between font-medium">
            <span>{account.label}</span>
            <span className="font-mono">{formatBytes(account.totalBytes)}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Cached market data and mailbox messages for this account. Data can be
            downloaded again from your broker when needed.
          </p>
        </CardContent>
      </Card>

      {marketType && (
        <div>
          <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            MARKET DATA
          </h3>
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={() => onClearType("marketData")}
                disabled={marketType.bytes === 0}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none"
              >
                <div className="flex items-center gap-3">
                  <LineChart className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Clear all market data</span>
                </div>
                <Badge variant="secondary" className="font-mono text-xs">
                  {formatBytes(marketType.bytes)}
                </Badge>
              </Button>
              {marketType.items && marketType.items.length > 0 && (
                <>
                  <Separator />
                  {marketType.items.map((item, i) => (
                    <div key={item.id}>
                      {i > 0 && <Separator />}
                      <Button
                        variant="ghost"
                        onClick={() => onClearSymbol(item.id)}
                        className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none pl-10"
                      >
                        <span className="text-sm">{item.label}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="font-mono text-xs">
                            {formatBytes(item.bytes)}
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </Button>
                    </div>
                  ))}
                </>
              )}
              {marketType.bytes === 0 && (
                <p className="text-sm text-muted-foreground p-3">
                  No cached chart data for this account.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {mailboxType && (
        <div>
          <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            MAILBOX
          </h3>
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={() => onClearType("mailbox")}
                disabled={mailboxType.bytes === 0}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = typeIcon("mailbox");
                    return <Icon className="h-4 w-4 text-muted-foreground" />;
                  })()}
                  <span className="text-sm font-medium">Mailbox messages</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {formatBytes(mailboxType.bytes)}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Button
            variant="ghost"
            onClick={onClearAccount}
            className="w-full h-auto p-3 text-destructive hover:text-destructive hover:bg-muted/50 rounded-lg"
          >
            Clear all cache for this account
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
